import {Badge, Box, Button, Stack} from '@mui/material';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import Typography from '@mui/material/Typography';
import React, {useCallback} from 'react';
import {useDropzone} from 'react-dropzone';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import PropTypes from 'prop-types';
import InfoHover from '../../../shared-components/InfoHover';
import useNotification from '../../../utility/hooks/useNotification';

const LogoUploader = ({onChange, value, compact}) => {
    const {showErrorNotification} = useNotification();

    const onDrop = useCallback((acceptedFiles) => {
        onChange(acceptedFiles[0]);
    }, []);

    const onDropRejected = useCallback((rejections) => {
        if (rejections.some(e => e.errors.some(error => error.code === 'file-too-large'))) {
            showErrorNotification('The size of the logo exceeds the limit of 100KB.');
        }
    }, []);

    const {getInputProps, open} = useDropzone({
        onDrop,
        onDropRejected,
        noClick: true,
        noKeyboard: true,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png'],
        },
        multiple: false,
        maxSize: 100 * 1024
    });

    const handleLogoRemove = () => onChange(null);

    // Maria, 20 October 2026: on School Setup the logo is a small box beside the school's name, labelled 'Logo' with an
    // 'i'; the file size and type are in the hover, so the line under the logo is not shown
    if (compact) {
        return (
            <Box
                sx={{
                    position: 'relative',
                    // the same top and bottom as the boxes beside it, which have 8px above and 4px below
                    height: 'calc(100% - 12px)',
                    minHeight: 88,
                    mt: 1,
                    mb: 0.5,
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: 1,
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': {borderColor: 'rgba(0, 0, 0, 0.87)'}
                }}
                onClick={open}
                data-testid="logo-box"
            >
                <Box
                    component="span"
                    sx={{
                        position: 'absolute',
                        top: -10,
                        left: 10,
                        px: 0.5,
                        backgroundColor: '#fff',
                        color: 'text.secondary',
                        // the same font as the 'School Name' label beside it
                        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                        fontSize: 12,
                        letterSpacing: '0.00938em',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}
                    onClick={event => event.stopPropagation()}
                >
                    School Logo
                    <InfoHover
                        testId="hover-logo"
                        text="Uploading your school's logo displays it in all emails sent by Admicity on behalf of your school. The file can be a JPEG or PNG of up to 100KB. Click on the box to choose the file."
                    />
                </Box>
                {value
                    ? (
                        <>
                            <img
                                src={URL.createObjectURL(value)}
                                alt="Logo preview"
                                style={{maxWidth: '85%', maxHeight: 70, objectFit: 'contain'}}
                            />
                            <IconButton
                                size="small"
                                aria-label="Delete logo"
                                sx={{position: 'absolute', top: 2, right: 2}}
                                onClick={event => {
                                    event.stopPropagation();
                                    handleLogoRemove();
                                }}
                            >
                                <CancelIcon fontSize="small"/>
                            </IconButton>
                        </>
                    )
                    : <InsertPhotoIcon color="disabled"/>}
                <input {...getInputProps()} />
            </Box>
        );
    }

    return (
        <Box marginTop={2}>
            {
                value
                    ? <Badge badgeContent={
                        <IconButton onClick={handleLogoRemove} size="small">
                            <CancelIcon fontSize="small"/>
                        </IconButton>
                    }>
                        <img
                            src={URL.createObjectURL(value)}
                            alt="Logo preview"
                            width={150}
                            style={{objectFit: 'cover'}}
                        />
                    </Badge>
                    : <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 100,
                            height: 100,
                            border: '1.5px dashed',
                            borderColor: 'grey.A400',
                            borderRadius: 2,
                            backgroundColor: 'grey.A100',
                            cursor: 'pointer'
                        }}
                        onClick={open}
                    >
                        <InsertPhotoIcon color={'disabled'}/>
                    </Box>
            }
            <input {...getInputProps()} />
            <Stack
                sx={{
                    '& .MuiTypography-root': {
                        margin: 0
                    }
                }}
                alignItems="center"
                flexDirection="row"
                marginTop={1}
            >
                <Box>
                    <Button sx={{color: '#186BBE'}} onClick={open}>Upload Logo</Button>
                </Box>
                <Typography color="text.secondary"> — Max file size 100KB</Typography>
            </Stack>
        </Box>
    );
};

LogoUploader.propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.object,
    compact: PropTypes.bool
};

export default LogoUploader;